import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, parse, eachWeekOfInterval, add as addDate, sub as subDate } from 'date-fns';
import { useParams } from 'react-router-dom';
import Fill from './Modals/Fill';
import Appointment from './Modals/Appointment';
import OptimisationIssues from './Modals/OptimisationIssues';
import ConfirmAppointments from './Modals/ConfirmAppointments';
import CompleteAppointments from './Modals/CompleteAppointments';
import ReplanDay from './Modals/ReplanDay';
import { useCalendar } from '../../Data/Calendar';
import { usePreference } from '../../Data/Preference';
import JobWithoutAppointment from './Modals/JobWithoutAppointment';
import { useRedirect, weekString } from '../../Services/helpers';
import ScheduleJob from '../Customer/ScheduleJob';
import { useGrants } from '../../Data/Business';
import { useScheduleSuggestion } from '../../Data/Jobs';
import ScheduleWeek from './Modals/ScheduleWeek';
import Aggregator from './New/Aggregator';

const CalendarContext = createContext();

export const useCalendarContext = () => {
    return useContext(CalendarContext);
};

const weekOpts = { weekStartsOn: 1 };

const maxWidth = 350;
const minWidth = 100;
const widthStep = 20;

const maxPxPerHour = 600;
const minPxPerHour = 50;
const pxPerHourStep = 40;

// New calendar
const appointmentWidth = 250;

export const Provider = ({ children, appointmentsContainerRef }) => {
    const {activeGrants} = useGrants();
    const {week: selectedWeek} = useParams();
    const currentWeek = parse(selectedWeek.replace('W', ''), 'RRRR-II', new Date());
    const [selectedWeekPreference, setSelectedWeekPreference] = usePreference('calendar:selectedWeek', weekString(), true);
    const {calendar, isLoading, generateWeek, confirmAppointments, completeAppointments, addMissingAppointments, clearAppointments, refresh: refreshCalendar} = useCalendar(selectedWeek, true);

    const [pxPerHour, setPxPerHour] = useState(100);
    const [width, setWidth] = useState(Math.min(maxWidth, Math.max(minWidth, Math.round(window.innerWidth * 0.8))));
    const [headerExpanded, setHeaderExpanded] = usePreference('calendar:headerExpanded', true);

    // New calendar
    const [newCalendar, setNewCalendar] = usePreference('calendar:new', true);
    const {dates: suggestions, refresh: refreshSuggestions} = useScheduleSuggestion(selectedWeek, true);
    const [suggestedJobs, setSuggestedJobs] = useState({});
    const [currentlyDragging, setCurrentlyDragging] = useState(null);
    const [moves, setMoves] = useState({});
    const [shownGrants, setShownGrants] = usePreference('calendar:shownGrants', {});
    const [durationSized, setDurationSized] = usePreference('calendar:durationSized', false);
    const [planningMode, setPlanningMode] = useState(null); // suggested / appointments

    // Modals
    const [filling, setFilling] = useState(false);
    const [previewingAppointment, setPreviewingAppointment] = useState(null);
    const [jobWithoutAppointment, setJobWithoutAppointment] = useState(null);
    const [replanningDay, setReplanningDay] = useState(null);
    const [optimisationIssuesOpen, setOptimisationIssuesOpen] = useState(false);
    const [lastOptimisationToken, setLastOptimisationToken] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [completing, setCompleting] = useState(null);
    const [schedulingJob, setSchedulingJob] = useState(null);
    const [schedulingDay, setSchedulingDay] = useState(null);
    const [schedulingWeek, setSchedulingWeek] = useState(null);

    const {go, Redirect} = useRedirect();

    const refresh = () => {
        return Promise.all([refreshCalendar(), refreshSuggestions()]);
    }

    useEffect(() => {
        if (!hasOptimisationIssues(calendar)) {
            return;
        }
        if (calendar?.lastOptimisationResult?.optimisationToken === lastOptimisationToken) {
            setOptimisationIssuesOpen(true);
            setLastOptimisationToken(null);
        }
    }, [lastOptimisationToken, calendar]);

    useEffect(() => {
        setMoves({});
        setPlanningMode(null);
    }, [selectedWeek]);

    useEffect(() => {
        if (!calendar?.appointments?.appointments) {
            setPlanningMode('suggested');
            return;
        }
        const hasAppointments = Object.values(calendar?.appointments?.appointments).reduce((carry, appointments) => {
            return carry || appointments.length > 0;
        }, false)

        setPlanningMode(hasAppointments ? 'appointments' : 'suggested');
    }, [calendar, planningMode]);

    useEffect(() => {
        if (!suggestions) {
            return;
        }

        const newSuggestedJobs = {};
        Object.keys(suggestions).forEach(date => {
            Object.keys(suggestions[date]).forEach(user => {
                const suggested = suggestions[date][user].suggested;
                newSuggestedJobs[date] ??= {};
                newSuggestedJobs[date][user] = [
                    ...suggested.jobs ?? [],
                    ...suggested.rounds.reduce((acc, round) => {
                        return [...acc, ...round.jobs];
                    }, [])
                ]
            });
        });

        setSuggestedJobs(newSuggestedJobs);
    }, [suggestions]);

    useEffect(() => {
        if (!activeGrants) {
            return;
        }

        activeGrants.forEach(grant => {
            if (typeof shownGrants[grant.id] !== 'boolean') {
                setShownGrants({...shownGrants, [grant.id]: true});
            }
        })
        if (typeof shownGrants['unassigned'] !== 'boolean') {
            setShownGrants({...shownGrants, 'unassigned': true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGrants]);

    // @deprecated - old calendar
    const handleResize = (direction) => {
        const container = appointmentsContainerRef.current;
        const oldScrollTop = container.scrollTop;
        const oldScrollHeight = container.scrollHeight;
        const scrollRatio = oldScrollTop / oldScrollHeight;

        setWidth(width => width + (widthStep * direction))

        setPxPerHour(size => {
            const newSize = size + (pxPerHourStep * direction);
            const useSize = Math.min(maxPxPerHour, Math.max(minPxPerHour, newSize));
            const changeRatio = useSize / size;

            if (newSize >= 0 && size >= 0) {
                const estScrollHeight = oldScrollHeight * changeRatio;
                const estScrollTop = estScrollHeight * scrollRatio;

                container.scrollTo({
                    top: estScrollTop,
                    behavior: 'auto'
                });
            }

            return newSize;
        });
    };

    const weekOptions = useMemo(() => {
        return eachWeekOfInterval({
            start: startOfWeek(subDate(currentWeek, {weeks: 2}), weekOpts),
            end: endOfWeek(addDate(currentWeek, {weeks: 8}), weekOpts)
        }).map(week => {
            const key = format(week, 'RRRR-II').replace('-', '-W');
            const normalTitle = format(startOfWeek(week, weekOpts), 'do LLL')+' - '+format(endOfWeek(week, weekOpts), 'do LLL');
            const isThisWeek = key === format(new Date(), 'RRRR-II').replace('-', '-W');

            return {
                title: isThisWeek ? 'This week' : normalTitle,
                onClick: () => {
                    setSelectedWeekPreference(key);
                    go('/plan/calendar/'+key);
                },
                value: key,
                // disabled: key === selectedWeek,
                isSelected: key === selectedWeek,
            };
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWeek]);

    const days = useMemo(() => {
        return eachDayOfInterval({
            start: startOfWeek(currentWeek, weekOpts),
            end: endOfWeek(currentWeek, weekOpts)
        }).map(day => {
            const today = format(new Date(), 'yyyy-MM-dd');
            const date = format(day, 'yyyy-MM-dd');
            return {
                title: date === today ? 'Today' : format(day, width >= 200 ? 'EEEE do' : (width > 100 ? 'EEE do' : 'EEE')),
                date: date,
                name: format(day, 'EEEE'),
                dayOfMonth: format(day, 'd'),
                isToday: date === today,
                isSelected: false,
                isPast: date < today
            };
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWeek, width]);

    // Jove moves in new calendar
    const moveJob = (jobId, customerId, userId, date) => {
        setMoves({...moves, [jobId]: {userId, customerId, jobId, date}});
    }
    const unmoveJob = (jobId) => {
        const newMoves = {...moves};
        delete newMoves[jobId];
        setMoves(newMoves);
    }
    const hasJobMoved = (jobId) => {
        return !!(moves[jobId] ?? false);
    }
    const jobsMovedTo = (userId, date) => {
        return Object.values(moves).filter(move => {
            if (userId === false) {
                return move.date === date;
            }

            return move.userId === userId && move.date === date
        });
    }
    const removeMoves = (userId, date) => {
        const newMoves = {...moves};
        Object.keys(moves).forEach(jobId => {
            if (moves[jobId].date !== date) {
                return;
            }
            if (userId === false || (userId === null && moves[jobId].userId === 'null') || (userId === moves[jobId].userId)) {
                delete newMoves[jobId];
            }
        });
        setMoves(newMoves);
    }

    const aggregateData = (limitToDate) => {
        return new Aggregator(
            days,
            activeGrants,
            shownGrants,
            planningMode,
            calendar,
            hasJobMoved,
            jobsMovedTo,
            suggestedJobs,
        ).aggregate(limitToDate);
    }

    const providerValues = {
        selectedWeek,
        currentWeek,
        calendar,
        isCalendarLoading: isLoading || !calendar,
        weekOptions,
        days,
        hasOptimisationIssues: hasOptimisationIssues(calendar),
        selectedWeekPreference,

        // @deprecated - old calendar
        dayWidth: Math.min(maxWidth, Math.max(minWidth, width)),
        pxPerHour: Math.min(maxPxPerHour, Math.max(minPxPerHour, pxPerHour)),
        handleResize,

        // New calendar
        newCalendar,
        setNewCalendar,
        currentlyDragging,
        setCurrentlyDragging,
        appointmentWidth,
        moveJob,
        unmoveJob,
        hasJobMoved,
        jobsMovedTo,
        removeMoves,
        clearMoves: () => setMoves({}),
        shownGrants,
        setShownGrants,
        suggestedJobs,
        durationSized,
        setDurationSized,
        planningMode,
        setPlanningMode,
        aggregateData,

        // Modals
        setPreviewingAppointment,
        setOptimisationIssuesOpen,
        setFilling,
        setReplanningDay,
        setConfirming,
        setCompleting,
        setJobWithoutAppointment,
        setSchedulingJob,
        setSchedulingDay,
        setSchedulingWeek,

        // Knowing if this is open helps us prevent appointment modals closing
        schedulingJob,

        headerExpanded: headerExpanded && !isLoading,
        toggleHeaderExpanded: () => setHeaderExpanded(!headerExpanded),
    };

    return (
        <CalendarContext.Provider value={providerValues}>
            {children}

            <Redirect />

            {/* Modals */}
            {calendar && <Fill {...{
                isOpen: filling,
                setIsOpen: setFilling,
                calendar,
                week: selectedWeek,
                generateWeek: (data) => { setLastOptimisationToken(data.optimisationToken); return generateWeek(data); },
                clearAppointments: async () => {
                    await clearAppointments({week: selectedWeek});
                    setFilling(false);
                    refresh();
                },
                refresh
            }} />}
            <OptimisationIssues isOpen={!!(optimisationIssuesOpen && hasOptimisationIssues(calendar))}
                setIsOpen={setOptimisationIssuesOpen}
                unableToSchedule={calendar?.lastOptimisationResult?.unableToSchedule}
            />
            <Appointment appointment={previewingAppointment} onClose={() => setPreviewingAppointment(null)} />
            <JobWithoutAppointment job={jobWithoutAppointment} onClose={() => setJobWithoutAppointment(null)} />
            <ReplanDay day={replanningDay}
                missingJobIds={replanningDay ? (calendar?.scheduledWithoutAppointments?.[replanningDay.date] ?? null) : null}
                availability={calendar?.availability}
                onClose={() => setReplanningDay(null)}
                addMissingAppointments={async (data) => {
                    setLastOptimisationToken(data.optimisationToken);
                    const ret = await addMissingAppointments(data);
                    removeMoves(false, data.date);
                    return ret;
                }}
                refresh={refresh}
            />
            <ConfirmAppointments isOpen={!!confirming}
                setIsOpen={setConfirming}
                confirming={confirming}
                confirmAppointments={confirmAppointments}
                refresh={refresh}
            />
            <CompleteAppointments isOpen={!!completing}
                setIsOpen={setCompleting}
                completing={completing}
                completeAppointments={completeAppointments}
                refresh={refresh}
            />
            <ScheduleWeek isOpen={!!schedulingWeek || (!!schedulingDay && newCalendar)}
                limitToDate={schedulingDay?.date}
                setIsOpen={() => { setSchedulingDay(null); setSchedulingWeek(null); }}
                then={async () => {
                    await refresh();
                    // removeMoves(schedulingDay.userId, schedulingDay.date);
                }}
            />

            <ScheduleJob job={schedulingJob}
                onClose={() => setSchedulingJob(null)}
                then={() => {
                    unmoveJob(schedulingJob.id);
                    refresh();
                }}
            />
        </CalendarContext.Provider>
    );
};

const hasOptimisationIssues = (calendar) => {
    if (!calendar?.lastOptimisationResult?.unableToSchedule) {
        return false;
    }

    const vals = Object.values(calendar.lastOptimisationResult.unableToSchedule);

    return vals.reduce((acc, val) => acc + val.length, 0) > 0;
}
